import React from "react";
import { Link as RouterLink } from "react-router-dom";
import Box from "@mui/joy/Box";
import LinearProgress from "@mui/joy/LinearProgress";
import Link from "@mui/joy/Link";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemButton from "@mui/joy/ListItemButton";
import Table from "@mui/joy/Table";
import Typography from "@mui/joy/Typography";
import { BarChart } from "@mui/x-charts/BarChart";
import { ScrollTable } from "../../../bids/analytics/components/AnalyticsShared";
import { AnalyticsSeriesChart } from "../../../bids/analytics/components/AnalyticsSeriesChart";
import { formatInt, toNum } from "../../../bids/analytics/format";
import { ChartGranularity } from "../../../../models/bidAnalytics";
import { OrderFailReasonsReport, OrderFailCulprit } from "../../../../models/orderAnalytics";
import {
    culpritItems,
    culpritLabel,
    failReasonLabel,
    failReasonListUrl,
    reasonBars,
    reasonGameMatrix,
    topReasonsDataset,
} from "../failReasons";
import { toggleKey, visibleSeries } from "../series";
import { SeriesToggle } from "../Shared";

export type ListContext = { gameId?: number; from: Date; to: Date };

const REASON_BAR_COLOR = "#c62828";
const REASON_ROW_HEIGHT = 30;

export function formatPercent(value: number | null | undefined): string {
    return value == null ? "—" : `${value.toLocaleString("ru-RU", { maximumFractionDigits: 1 })}%`;
}

/** Ссылка в список ордеров или просто текст, если перейти нельзя (без причины) */
export function ListLink({ url, children, color }: {
    url: string | null;
    children: React.ReactNode;
    color?: "primary" | "neutral";
}) {
    // Переходы из аналитики открываются в новом окне, чтобы не терять фильтры и раскрытые строки
    return url
        ? <Link component={RouterLink} to={url} color={color} target="_blank" rel="noopener noreferrer">{children}</Link>
        : <>{children}</>;
}

/** Причины по количеству: горизонтальные столбцы, полная подпись причины слева */
export function ReasonsBarChart({ report }: { report: OrderFailReasonsReport }) {
    const bars = reasonBars(report.reasons);
    return (
        <Box sx={{ width: "100%", overflowX: "auto" }}>
            <Box sx={{ minWidth: 640 }}>
                <BarChart
                    layout="horizontal"
                    height={Math.max(160, bars.labels.length * REASON_ROW_HEIGHT + 60)}
                    yAxis={[{ scaleType: "band", data: bars.labels, tickLabelStyle: { fontSize: 12 } }]}
                    series={[{
                        data: bars.counts, label: "Неуспешные", color: REASON_BAR_COLOR,
                        valueFormatter: (v: number | null) => formatInt(v ?? 0),
                    }]}
                    margin={{ left: 300, right: 30, top: 40, bottom: 30 }}
                    slotProps={{ legend: { hidden: true } }}
                />
            </Box>
        </Box>
    );
}

/** Виновники списком с полоской доли; выбранный раскрывается до причин */
export function CulpritBreakdown({ report, ctx }: { report: OrderFailReasonsReport; ctx: ListContext }) {
    const items = culpritItems(report.byCulprit);
    const [chosen, setSelected] = React.useState<OrderFailCulprit | undefined>(items[0]?.id);
    // После смены фильтров выбранного виновника может не быть в отчёте — тогда показываем первого
    const selected = report.byCulprit.some((c) => c.culprit === chosen) ? chosen : items[0]?.id;
    const culprit = report.byCulprit.find((c) => c.culprit === selected);

    return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-start" }}>
            <List size="sm" sx={{ minWidth: 300, flex: "0 1 360px" }} aria-label="Виновники">
                {items.map((item) => (
                    <ListItem key={item.id}>
                        <ListItemButton selected={item.id === selected} aria-pressed={item.id === selected}
                                        onClick={() => setSelected(item.id)}
                                        sx={{ flexDirection: "column", alignItems: "stretch", gap: 0.5 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                                <Typography level="body-sm">{item.label}</Typography>
                                <Typography level="body-sm" fontWeight="lg">
                                    {formatInt(item.value)} ({formatPercent(item.percent)})
                                </Typography>
                            </Box>
                            <LinearProgress determinate value={Math.min(100, item.percent)} thickness={6}
                                            sx={{ color: item.color, bgcolor: "background.level2" }} />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
            {culprit && (
                <Box sx={{ flex: "1 1 320px", minWidth: 0 }}>
                    <Typography level="title-sm" sx={{ mb: 1 }}>
                        Причины: {culpritLabel(culprit.culprit)}
                    </Typography>
                    <Table size="sm">
                        <thead>
                            <tr>
                                <th scope="col">Причина</th>
                                <th scope="col" style={{ width: 120 }}>Количество</th>
                            </tr>
                        </thead>
                        <tbody>
                            {culprit.reasons.map((r) => (
                                <tr key={String(r.failReason ?? "none")}>
                                    <td>
                                        <ListLink url={failReasonListUrl(r.failReason, ctx)}>
                                            {failReasonLabel(r.failReason)}
                                        </ListLink>
                                    </td>
                                    <td>{formatInt(r.count)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Box>
            )}
        </Box>
    );
}

/** Причина × игра: чем насыщеннее цвет, тем больше неуспешных; число — ссылка в список с этой игрой */
export function ReasonGameHeatmap({ report, ctx }: { report: OrderFailReasonsReport; ctx: ListContext }) {
    const matrix = reasonGameMatrix(report.reasonByGame, report.reasons);
    // Не насыщеннее 0.35, чтобы число-ссылка оставалось читаемым
    const alpha = (count: number) => (matrix.max > 0 ? 0.06 + (count / matrix.max) * 0.29 : 0);

    return (
        <ScrollTable>
            <Table size="sm" sx={{ minWidth: 300 + matrix.games.length * 110 }}>
                <thead>
                    <tr>
                        <th scope="col" style={{ width: 280 }}>Причина</th>
                        {matrix.games.map((g) => (
                            <th scope="col" key={g.gameId}>{g.gameName}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {matrix.rows.map((row) => (
                        <tr key={String(row.failReason ?? "none")}>
                            <th scope="row" style={{ fontWeight: 400 }}>{failReasonLabel(row.failReason)}</th>
                            {row.counts.map((count, i) => (
                                <td key={matrix.games[i].gameId}
                                    style={{ backgroundColor: count > 0 ? `rgba(198, 40, 40, ${alpha(count)})` : undefined }}>
                                    {count > 0
                                        ? (
                                            <ListLink color="neutral" url={failReasonListUrl(row.failReason,
                                                { ...ctx, gameId: matrix.games[i].gameId })}>
                                                {formatInt(count)}
                                            </ListLink>
                                        )
                                        : "—"}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </Table>
        </ScrollTable>
    );
}

/** Линии топ-5 причин и «прочих» по времени; переключатели скрывают и показывают линии */
export function TopReasonsChart({ report, granularity }: {
    report: OrderFailReasonsReport;
    granularity: ChartGranularity;
}) {
    const { dataset, series } = topReasonsDataset(report.series, report.topReasons, granularity);
    const [hidden, setHidden] = React.useState<ReadonlySet<string>>(new Set());
    return (
        <>
            <SeriesToggle series={series} hidden={hidden} onToggle={(key) => setHidden((h) => toggleKey(h, key))} />
            <AnalyticsSeriesChart
                dataset={dataset}
                granularity={granularity}
                height={320}
                series={visibleSeries(series, hidden)
                    .map((s) => ({ ...s, valueFormatter: (v: number | null) => formatInt(toNum(v)) }))}
            />
        </>
    );
}
